import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { OperationsBanner } from "@/components/staff/OperationsBanner";
import { STAFF_NAV } from "@/lib/staff-nav";
import { supabase } from "@/integrations/supabase/client";
import { Eyebrow } from "@/components/brand/Eyebrow";

export const Route = createFileRoute("/staff")({
  head: () => ({
    meta: [
      { title: "Operations Portal — Currency Thoughts" },
      { name: "description", content: "Scheduling, leads routing, tasks, and support operations for Currency Thoughts." },
      { property: "og:title", content: "Operations Portal — Currency Thoughts" },
      { property: "og:description", content: "Scheduling, leads routing, tasks, and support operations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StaffPortal,
});

function StaffPortal() {
  const location = useLocation();
  const isOverview = location.pathname === "/staff";
  return (
    <ProtectedRoute requiredRole="staff">
      {isOverview ? (
        <PortalShell title="Operations Portal" nav={STAFF_NAV} headerVariant="navy">
          <StaffOverview />
        </PortalShell>
      ) : (
        <Outlet />
      )}
    </ProtectedRoute>
  );
}

interface Counts {
  leads: number;
  appointments: number;
  tasks: number;
  clients: number;
}

function StaffOverview() {
  const [counts, setCounts] = useState<Counts>({ leads: 0, appointments: 0, tasks: 0, clients: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const [l, a, t, c] = await Promise.all([
        supabase.from("leads").select("id", { count: "exact", head: true }),
        supabase.from("appointments").select("id", { count: "exact", head: true }).gte("scheduled_at", new Date().toISOString()),
        supabase.from("tasks").select("id", { count: "exact", head: true }).neq("status", "done"),
        supabase.from("staff_client_directory").select("id", { count: "exact", head: true }),
      ]);
      setCounts({
        leads: l.count ?? 0,
        appointments: a.count ?? 0,
        tasks: t.count ?? 0,
        clients: c.count ?? 0,
      });
      setLoading(false);
    })();
  }, []);

  const cards = [
    { label: "Leads in queue", value: counts.leads },
    { label: "Upcoming appointments", value: counts.appointments },
    { label: "Open tasks", value: counts.tasks },
    { label: "Household directory", value: counts.clients },
  ];

  return (
    <div>
      <OperationsBanner />
      <Eyebrow>Operations</Eyebrow>
      <h1 className="mt-3 font-serif text-3xl font-semibold text-navy-900 sm:text-4xl">Today at a glance</h1>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-sm border border-bronze-600/30 bg-white p-4 shadow-[0_1px_3px_rgba(168,139,92,0.08)]">
            <p className="font-serif text-3xl font-semibold text-navy-900">{loading ? "—" : c.value}</p>
            <p className="mt-1 font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">{c.label}</p>
          </div>
        ))}
      </div>
      <p className="mt-8 font-sans text-sm leading-relaxed text-charcoal-500">
        Operations covers scheduling, routing, paperwork, and support. Compensation, policy detail,
        needs analysis, and team structure are intentionally not available in this portal.
      </p>
    </div>
  );
}
